import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  Observable,
  of
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  finalize,
  map,
  shareReplay,
  startWith,
  switchMap
} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

import { ApiService } from '../../../services/api.service';
import { Attachment } from '../../../models/attachment';


export interface CarouselAttachmentItem {
  attachment: Attachment;
  url: string | null;
  loading: boolean;
  error: boolean;
}


interface ImagesState {
  images: CarouselAttachmentItem[];
  isLoading: boolean;
}


interface ViewModel {
  images: CarouselAttachmentItem[];
  currentIndex: number;
  currentImage: CarouselAttachmentItem | null;
  isLoading: boolean;
}


export type CarouselNavigationMode = 'dots' | 'thumbnails' | 'none';


@Component({
  selector: 'attachments-carousel',
  templateUrl: './attachments-carousel.component.html',
  styleUrls: ['./attachments-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AttachmentsCarouselComponent {

  @ViewChild('pz') pzDirective?: any;

  private readonly attachmentsSubject =
    new BehaviorSubject<Attachment[]>([]);

  private readonly organizationIdSubject =
    new BehaviorSubject<string | null>(null);

  private readonly currentIndexSubject =
    new BehaviorSubject<number>(0);

  private readonly refreshUrlsSubject =
    new BehaviorSubject<number>(0);

  private readonly favoriteFilenameSubject =
    new BehaviorSubject<string | null>(null);

  private readonly imageRetryCount: Record<string, number> = {};

  private imgEl?: HTMLImageElement;

  imgNatural?: { w: number; h: number };
  overlayPolygonPoints: string | null = null;

  loadedImages: Record<string, boolean> = {};
  downloading: Record<string, boolean> = {};

  rotation = 0;

  sceneWidth?: number;
  sceneHeight?: number;
  mediaTransform = '';

  private _organizationId: string | null = null;


  @Input() enablePanzoom = true;
  @Input() showOverlay = true;
  @Input() allowDownload = true;
  @Input() allowRotate = true;
  @Input() allowFavorite = false;
  @Input() showSVG = true;
  @Input() navigationMode: CarouselNavigationMode = 'dots';


  @Input()
  set favoriteFilename(value: string | null | undefined) {
    const filename = value ?? null;

    this.favoriteFilenameSubject.next(filename);

    if (!filename) {
      return;
    }

    const attachments = this.attachmentsSubject.value;

    const favoriteIndex = attachments.findIndex(
      attachment => attachment.filename === filename
    );

    if (favoriteIndex >= 0) {
      this.selectIndex(favoriteIndex);
    }
  }

  get favoriteFilename(): string | null {
    return this.favoriteFilenameSubject.value;
  }


  @Input({ required: true })
  set attachments(value: Attachment[] | null | undefined) {
    const attachments = value ?? [];

    this.attachmentsSubject.next(attachments);
    this.selectInitialImage(attachments);
    this.resetImageState();
  }


  @Input({ required: true })
  set organizationId(value: string | null | undefined) {
    this._organizationId = value ?? null;
    this.organizationIdSubject.next(value ?? null);
  }


  @Output() favoriteChange = new EventEmitter<Attachment>();
  @Output() contentHeightChanged = new EventEmitter<void>();


  readonly images$ = combineLatest([
    this.attachmentsSubject.pipe(
      distinctUntilChanged(
        (a, b) => JSON.stringify(a) === JSON.stringify(b)
      )
    ),
    this.organizationIdSubject.pipe(distinctUntilChanged()),
    this.refreshUrlsSubject
  ]).pipe(
    switchMap(([attachments, organizationId]) => {
      if (!attachments.length || !organizationId) {
        return of<ImagesState>({
          images: [],
          isLoading: false
        });
      }

      const requests$ = attachments.map((attachment) =>
        this.api.resourceTmpUrl$(
          organizationId,
          attachment.path,
          attachment.filename
        ).pipe(
          map(
            (url: string | null): CarouselAttachmentItem => ({
              attachment,
              url,
              loading: false,
              error: !url
            })
          ),
          catchError(() =>
            of<CarouselAttachmentItem>({
              attachment,
              url: null,
              loading: false,
              error: true
            })
          )
        )
      );

      return forkJoin(requests$).pipe(
        map(
          (images): ImagesState => ({
            images,
            isLoading: false
          })
        ),
        startWith<ImagesState>({
          images: [],
          isLoading: true
        })
      );
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true
    })
  );


  readonly vm$ = combineLatest([
    this.images$,
    this.currentIndexSubject
  ]).pipe(
    map(([state, currentIndex]): ViewModel => {
      const images = state.images;

      const safeIndex =
        images.length === 0
          ? 0
          : Math.min(currentIndex, images.length - 1);

      return {
        images,
        currentIndex: safeIndex,
        currentImage: images[safeIndex] ?? null,
        isLoading: state.isLoading
      };
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true
    })
  );


  constructor(
    private readonly api: ApiService,
    private readonly http: HttpClient,
    private readonly cdr: ChangeDetectorRef
  ) {}

  private selectIndex(index: number): void {
    if (this.currentIndexSubject.value === index) {
      return;
    }

    this.currentIndexSubject.next(index);

    this.rotation = 0;
    this.resetImageState();

    requestAnimationFrame(() => {
      this.updatePanzoomFit();
    });

    this.cdr.markForCheck();
  }


  isFavorite(item: CarouselAttachmentItem): boolean {
    return item.attachment.filename === this.favoriteFilename;
  }


  toggleFavorite(item: CarouselAttachmentItem): void {
    if (this.isFavorite(item)) {
      return;
    }

    this.favoriteChange.emit(item.attachment);
  }


  onImageLoad(
    img: HTMLImageElement,
    item: CarouselAttachmentItem
  ): void {
    this.imgEl = img;

    this.imgNatural = {
      w: img.naturalWidth,
      h: img.naturalHeight
    };

    this.updateRotationLayout();

    this.loadedImages = {
      ...this.loadedImages,
      [item.attachment.filename]: true
    };

    delete this.imageRetryCount[item.attachment.filename];

    this.buildOverlayFromBoundingBox(item.attachment);

    requestAnimationFrame(() => {
      this.updatePanzoomFit();
      this.contentHeightChanged.emit();
    });

    this.cdr.markForCheck();
  }


  onImageError(item: CarouselAttachmentItem): void {
    const filename = item.attachment.filename;
    const retries = this.imageRetryCount[filename] ?? 0;

    if (retries >= 1) {
      return;
    }

    this.imageRetryCount[filename] = retries + 1;
    this.refreshUrls();
  }


  goTo(index: number): void {
    this.selectIndex(index);
  }


  downloadImage(item: CarouselAttachmentItem): void {
    const attachment = item.attachment;

    if (
      !attachment ||
      !this._organizationId ||
      this.downloading[attachment.path]
    ) {
      return;
    }

    const downloadKey = attachment.path;

    const filename =
      attachment.filename ||
      attachment.path.split('/').pop() ||
      'image';

    const extension = this.getExtension(filename);

    this.downloading = {
      ...this.downloading,
      [downloadKey]: true
    };

    this.cdr.markForCheck();

    this.api.resourceTmpUrl$(
      this._organizationId,
      attachment.path,
      filename
    ).pipe(
      switchMap((url): Observable<Blob | null> => {
        if (!url) {
          return of(null);
        }

        const shouldExportWithBoundingBox =
          this.showOverlay &&
          this.showSVG &&
          !!attachment.polygon?.length;

        if (shouldExportWithBoundingBox) {
          return this.exportImageUrlWithBoundingBox$(
            url,
            extension,
            attachment.polygon ?? [],
            attachment.polygonHexColor ?? '#ff3b30'
          );
        }

        return this.http.get(
          url,
          {
            responseType: 'blob'
          }
        );
      }),
      catchError(() => of(null)),
      finalize(() => {
        const {
          [downloadKey]: _,
          ...rest
        } = this.downloading;

        this.downloading = rest;
        this.cdr.markForCheck();
      })
    ).subscribe((blob) => {
      if (!(blob instanceof Blob)) {
        return;
      }

      this.downloadBlob(blob, filename);
    });
  }


  trackByFilename(
    _: number,
    item: CarouselAttachmentItem
  ): string {
    return item.attachment.filename;
  }


  rotateRight(): void {
    this.rotation = (this.rotation + 90) % 360;

    this.updateRotationLayout();

    requestAnimationFrame(() => {
      this.updatePanzoomFit();
    });
  }


  toggleSVG(): void {
    this.showSVG = !this.showSVG;
  }


  onThumbnailsWheel(event: WheelEvent): void {
    const container = event.currentTarget as HTMLElement;

    if (!container) {
      return;
    }

    event.preventDefault();

    container.scrollLeft += event.deltaY;
  }


  private selectInitialImage(
    attachments: Attachment[]
  ): void {
    const favoriteFilename =
      this.favoriteFilenameSubject.value;

    if (!favoriteFilename) {
      this.selectIndex(0);
      return;
    }

    const favoriteIndex = attachments.findIndex(
      attachment =>
        attachment.filename === favoriteFilename
    );

    this.selectIndex(
      favoriteIndex >= 0 ? favoriteIndex : 0
    );
  }


  private updateRotationLayout(): void {
    if (!this.imgNatural) {
      this.sceneWidth = undefined;
      this.sceneHeight = undefined;
      this.mediaTransform = '';
      return;
    }

    const { w, h } = this.imgNatural;

    if (this.rotation === 0) {
      this.sceneWidth = w;
      this.sceneHeight = h;
      this.mediaTransform =
        'translate(0px, 0px) rotate(0deg)';
      return;
    }

    if (this.rotation === 90) {
      this.sceneWidth = h;
      this.sceneHeight = w;
      this.mediaTransform =
        `translate(${h}px, 0px) rotate(90deg)`;
      return;
    }

    if (this.rotation === 180) {
      this.sceneWidth = w;
      this.sceneHeight = h;
      this.mediaTransform =
        `translate(${w}px, ${h}px) rotate(180deg)`;
      return;
    }

    this.sceneWidth = h;
    this.sceneHeight = w;
    this.mediaTransform =
      `translate(0px, ${w}px) rotate(270deg)`;
  }


  private refreshUrls(): void {
    this.refreshUrlsSubject.next(
      this.refreshUrlsSubject.value + 1
    );
  }


  private resetImageState(): void {
    this.imgEl = undefined;
    this.imgNatural = undefined;
    this.overlayPolygonPoints = null;

    this.sceneWidth = undefined;
    this.sceneHeight = undefined;
    this.mediaTransform = '';
  }


  private updatePanzoomFit(): void {
    if (!this.enablePanzoom || !this.pzDirective) {
      return;
    }

    requestAnimationFrame(() => {
      this.pzDirective.fitToWrapper();
    });
  }


  private buildOverlayFromBoundingBox(
    attachment: Attachment
  ): void {
    this.overlayPolygonPoints = null;

    const points = attachment.polygon;

    if (!points || points.length < 2) {
      return;
    }

    let polygon: Array<[number, number]>;

    if (points.length === 2) {
      const [[x1, y1], [x2, y2]] = points;

      const left = Math.min(x1, x2);
      const right = Math.max(x1, x2);
      const top = Math.min(y1, y2);
      const bottom = Math.max(y1, y2);

      polygon = [
        [left, top],
        [right, top],
        [right, bottom],
        [left, bottom]
      ];
    } else {
      polygon = points;
    }

    this.overlayPolygonPoints = polygon
      .map(([x, y]) => `${x},${y}`)
      .join(' ');
  }


  private exportImageUrlWithBoundingBox$(
    url: string,
    extension: string,
    boundingBox: Array<[number, number]>,
    color: string
  ): Observable<Blob | null> {
    return this.http.get(
      url,
      {
        responseType: 'blob'
      }
    ).pipe(
      switchMap(
        (blob) =>
          this.drawBoundingBoxOnImage$(
            blob,
            extension,
            boundingBox,
            color
          )
      )
    );
  }


  private drawBoundingBoxOnImage$(
    blob: Blob,
    extension: string,
    boundingBox: Array<[number, number]>,
    color: string
  ): Observable<Blob | null> {
    return new Observable<Blob | null>((observer) => {
      const objectUrl = URL.createObjectURL(blob);
      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(objectUrl);

        const canvas =
          document.createElement('canvas');

        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;

        const ctx = canvas.getContext('2d');

        if (!ctx) {
          observer.next(null);
          observer.complete();
          return;
        }

        ctx.drawImage(
          image,
          0,
          0,
          image.naturalWidth,
          image.naturalHeight
        );

        const polygon =
          this.normalizeToPolygon(boundingBox);

        if (polygon.length >= 2) {
          ctx.beginPath();

          ctx.moveTo(
            polygon[0][0],
            polygon[0][1]
          );

          for (
            let i = 1;
            i < polygon.length;
            i++
          ) {
            ctx.lineTo(
              polygon[i][0],
              polygon[i][1]
            );
          }

          ctx.closePath();
          ctx.lineWidth = 10;
          ctx.strokeStyle = color;
          ctx.stroke();
        }

        canvas.toBlob(
          (outputBlob) => {
            observer.next(outputBlob);
            observer.complete();
          },
          this.getMimeType(extension)
        );
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        observer.next(null);
        observer.complete();
      };

      image.src = objectUrl;
    });
  }


  private normalizeToPolygon(
    points: Array<[number, number]>
  ): Array<[number, number]> {
    if (!points || points.length < 2) {
      return [];
    }

    if (points.length === 2) {
      const [[x1, y1], [x2, y2]] = points;

      const left = Math.min(x1, x2);
      const right = Math.max(x1, x2);
      const top = Math.min(y1, y2);
      const bottom = Math.max(y1, y2);

      return [
        [left, top],
        [right, top],
        [right, bottom],
        [left, bottom]
      ];
    }

    return points;
  }


  private downloadBlob(
    blob: Blob,
    filename: string
  ): void {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = objectUrl;
    link.download =
      filename.replace(/ /g, '_');
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(objectUrl);
  }


  private getExtension(
    filename: string
  ): string {
    const parts = filename.split('.');

    return parts.length > 1
      ? parts.pop()!.toLowerCase()
      : 'png';
  }


  private getMimeType(
    extension: string
  ): string {
    switch (extension.toLowerCase()) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';

      case 'webp':
        return 'image/webp';

      case 'png':
      default:
        return 'image/png';
    }
  }


  @HostListener('window:resize')
  onWindowResize(): void {
    requestAnimationFrame(() => {
      this.updatePanzoomFit();
    });
  }
}
